import { useState } from "react";

/**
 * Shared consent + checkout payload for /payments/checkout callsites.
 *
 * Backend (POST /api/payments/checkout) requires ALL three flags true:
 *   terms_accepted
 *   privacy_acknowledged
 *   immediate_service_requested
 *
 * The legal UX uses TWO checkboxes:
 *   • checkbox 1 = Terms of Service + Privacy Policy (single opt-in, links open in new tab)
 *   • checkbox 2 = Immediate service before 14-day withdrawal period
 * When both are checked, all three backend flags are set to true in the request.
 *
 * Copy + links + data-testid pattern are lifted verbatim from
 * WhatNextPaywall.jsx / Pricing.jsx so the two paywalls already in
 * production and the ones migrated onto this helper stay 1:1 identical.
 *
 * Checkboxes MUST start unchecked. Never default consent=true.
 */

export function useCheckoutConsents() {
  const [consents, setConsents] = useState({});
  const consentOf = (id) => consents[id] || { terms: false, immediate: false };
  const setConsent = (id, key, val) =>
    setConsents((prev) => ({ ...prev, [id]: { ...consentOf(id), [key]: val } }));
  const canBuy = (id) => {
    const c = consentOf(id);
    return c.terms === true && c.immediate === true;
  };
  return { consentOf, setConsent, canBuy };
}

export function CheckoutConsentCheckboxes({ lang, planId, consent, onChange, testidPrefix = "consent" }) {
  const isEN = lang === "en";
  return (
    <>
      <label className="mt-4 flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          data-testid={`${testidPrefix}-terms-${planId}`}
          checked={consent.terms}
          onChange={(e) => onChange("terms", e.target.checked)}
          className="mt-0.5 shrink-0"
        />
        <span>
          {isEN ? (
            <>I accept the{" "}
              <a href="/pricing#terms" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`${testidPrefix}-terms-link-${planId}`}>LEO Terms of Service</a>
              {" "}and confirm that I have read the{" "}
              <a href="/pricing#privacy" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`${testidPrefix}-privacy-link-${planId}`}>Privacy Policy</a>.
            </>
          ) : (
            <>Akceptuję{" "}
              <a href="/pricing#terms" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`${testidPrefix}-terms-link-${planId}`}>Regulamin LEO</a>
              {" "}i potwierdzam zapoznanie się z{" "}
              <a href="/pricing#privacy" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`${testidPrefix}-privacy-link-${planId}`}>Polityką Prywatności</a>.
            </>
          )}
        </span>
      </label>
      <label className="mt-2 flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          data-testid={`${testidPrefix}-immediate-${planId}`}
          checked={consent.immediate}
          onChange={(e) => onChange("immediate", e.target.checked)}
          className="mt-0.5 shrink-0"
        />
        <span>
          {isEN
            ? "I request that the LEO service begins immediately after purchase, before the expiry of the 14-day withdrawal period. I acknowledge that if I exercise my right of withdrawal, I may be required to pay for the service provided up to the time of withdrawal, in accordance with applicable law."
            : "Żądam rozpoczęcia świadczenia usługi LEO od razu po zakupie, przed upływem 14-dniowego terminu na odstąpienie od umowy. Przyjmuję do wiadomości, że w przypadku odstąpienia mogę być zobowiązany/a do zapłaty za świadczenie spełnione do chwili odstąpienia, zgodnie z obowiązującymi przepisami."}
        </span>
      </label>
    </>
  );
}

/** Canonical checkout body: always sets all three backend consent flags. */
export function checkoutPayload(lookup_key) {
  return {
    lookup_key,
    origin_url: window.location.origin,
    terms_accepted: true,
    privacy_acknowledged: true,
    immediate_service_requested: true,
  };
}
